import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Clock, XCircle, ChevronRight, ListTodo, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';

interface PlanStep {
  id: string;
  title: string;
  description: string;
  actionType: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  order: number;
  notes?: string;
  completedAt?: string;
}

interface Plan {
  id: string;
  title: string;
  goal?: string;
  status: 'draft' | 'active' | 'completed' | 'abandoned';
  stepCount: number;
  completedStepCount: number;
  createdAt: string;
  updatedAt: string;
  steps?: PlanStep[];
}

export function PlansPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('plans:write');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
  });

  const { data: planDetailData, isLoading: planDetailLoading } = useQuery({
    queryKey: ['plan', selectedPlanId],
    queryFn: () => api.getPlan(selectedPlanId!),
    enabled: !!selectedPlanId,
  });

  const updateStepMutation = useMutation({
    mutationFn: ({ planId, stepId, status }: { planId: string; stepId: string; status: string }) =>
      api.updatePlanStepStatus(planId, stepId, status),
    onSuccess: (_data, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['plan', selectedPlanId] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      const labels: Record<string, string> = { in_progress: 'Step started', completed: 'Step completed', skipped: 'Step skipped' };
      toast.success(labels[status] ?? 'Step updated');
    },
    onError: () => {
      toast.error('Failed to update step');
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ planId, status }: { planId: string; status: string }) =>
      api.updatePlanStatus(planId, status),
    onSuccess: (_data, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['plan', selectedPlanId] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      const labels: Record<string, string> = { active: 'Action plan started', abandoned: 'Action plan abandoned', completed: 'Action plan completed' };
      toast.success(labels[status] ?? 'Plan updated');
    },
    onError: () => {
      toast.error('Failed to update plan');
    },
  });

  const plans: Plan[] = plansData?.data ?? [];
  const selectedPlan: Plan | undefined = planDetailData?.data;

  const handleStepStatusChange = (stepId: string, newStatus: string) => {
    if (!canWrite || !selectedPlanId) return;
    updateStepMutation.mutate({ planId: selectedPlanId, stepId, status: newStatus });
  };

  const handlePlanStatusChange = (newStatus: string) => {
    if (!canWrite || !selectedPlanId) return;
    updatePlanMutation.mutate({ planId: selectedPlanId, status: newStatus });
  };

  if (plansLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Action Plans</h1>
        <p className="text-gray-600">Execute your wealth strategy step by step</p>
      </div>

      {plans.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={ListTodo}
            title="No action plans yet"
            description="Accept recommendations first, then create an action plan to track your progress step by step."
            action={{ label: 'Review Recommendations', href: '/recommendations' }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Plans List */}
          <div className="lg:col-span-1 space-y-3">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlanId(plan.id)}
                className={clsx(
                  'card cursor-pointer transition-all',
                  selectedPlanId === plan.id
                    ? 'ring-2 ring-legacy-500 bg-legacy-50'
                    : 'hover:shadow-md'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{plan.title}</h3>
                    {plan.goal && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{plan.goal}</p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <PlanStatusBadge status={plan.status} />
                  <span className="text-xs text-gray-500">
                    {plan.completedStepCount}/{plan.stepCount} steps
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-legacy-500 transition-all"
                    style={{
                      width: `${plan.stepCount > 0 ? (plan.completedStepCount / plan.stepCount) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Plan Detail */}
          <div className="lg:col-span-2">
            {selectedPlanId ? (
              planDetailLoading ? (
                <div className="card flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : selectedPlan ? (
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{selectedPlan.title}</h2>
                      {selectedPlan.goal && (
                        <p className="text-gray-600 mt-1">{selectedPlan.goal}</p>
                      )}
                    </div>
                    <PlanStatusBadge status={selectedPlan.status} />
                  </div>

                  {/* Plan Actions */}
                  {selectedPlan.status === 'draft' && (
                    <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-800">
                        This plan is ready to go. Activate it to start tracking your progress.
                      </p>
                      <button
                        onClick={() => handlePlanStatusChange('active')}
                        disabled={updatePlanMutation.isPending}
                        className="mt-2 px-3 py-1.5 bg-legacy-600 text-white text-sm rounded hover:bg-legacy-700 disabled:opacity-50"
                      >
                        {updatePlanMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Start This Plan'
                        )}
                      </button>
                    </div>
                  )}

                  {selectedPlan.status === 'active' && (
                    <div className="mb-4 flex gap-2">
                      <button
                        onClick={() => setShowAbandonConfirm(true)}
                        disabled={updatePlanMutation.isPending}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 disabled:opacity-50"
                      >
                        Abandon Plan
                      </button>
                    </div>
                  )}

                  {/* Steps */}
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900">Steps</h3>
                    {selectedPlan.steps && selectedPlan.steps.length > 0 ? (
                      <div className="space-y-2">
                        {selectedPlan.steps
                          .sort((a, b) => a.order - b.order)
                          .map((step) => (
                            <StepCard
                              key={step.id}
                              step={step}
                              planStatus={selectedPlan.status}
                              onStatusChange={(status) => handleStepStatusChange(step.id, status)}
                              isUpdating={updateStepMutation.isPending}
                            />
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No steps defined for this plan.</p>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <p>Created: {new Date(selectedPlan.createdAt).toLocaleString()}</p>
                    <p>Updated: {new Date(selectedPlan.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              ) : (
                <div className="card text-center py-12 text-gray-500">
                  <p>Plan not found</p>
                </div>
              )
            ) : (
              <div className="card text-center py-12 text-gray-500">
                <p>Select an action plan to view its steps</p>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showAbandonConfirm}
        title="Abandon plan"
        description="Are you sure you want to abandon this plan? All progress will be preserved but the plan will be marked as abandoned."
        confirmLabel="Abandon"
        destructive
        onConfirm={() => {
          handlePlanStatusChange('abandoned');
          setShowAbandonConfirm(false);
        }}
        onCancel={() => setShowAbandonConfirm(false)}
      />
    </div>
  );
}

interface StepCardProps {
  step: PlanStep;
  planStatus: string;
  onStatusChange: (status: string) => void;
  isUpdating: boolean;
}

function StepCard({ step, planStatus, onStatusChange, isUpdating }: StepCardProps) {
  const isActive = planStatus === 'active';

  const statusIcons = {
    pending: <Circle className="h-5 w-5 text-gray-400" />,
    in_progress: <Clock className="h-5 w-5 text-blue-500" />,
    completed: <CheckCircle className="h-5 w-5 text-green-500" />,
    skipped: <XCircle className="h-5 w-5 text-gray-400" />,
  };

  const nextStatus: Record<string, string> = {
    pending: 'in_progress',
    in_progress: 'completed',
  };

  return (
    <div
      className={clsx(
        'p-4 rounded-lg border transition-all',
        step.status === 'completed'
          ? 'bg-green-50 border-green-200'
          : step.status === 'in_progress'
            ? 'bg-blue-50 border-blue-200'
            : step.status === 'skipped'
              ? 'bg-gray-50 border-gray-200'
              : 'bg-white border-gray-200'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{statusIcons[step.status]}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h4
                className={clsx(
                  'font-medium',
                  step.status === 'completed' || step.status === 'skipped'
                    ? 'text-gray-500 line-through'
                    : 'text-gray-900'
                )}
              >
                {step.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1">{step.description}</p>
            </div>
            <span className="text-xs text-gray-400 uppercase ml-2">{step.actionType}</span>
          </div>

          {/* Step Actions */}
          {isActive && step.status !== 'completed' && step.status !== 'skipped' && (
            <div className="mt-3 flex gap-2">
              {nextStatus[step.status] && (
                <button
                  onClick={() => onStatusChange(nextStatus[step.status])}
                  disabled={isUpdating}
                  className="px-3 py-1 bg-legacy-600 text-white text-xs rounded hover:bg-legacy-700 disabled:opacity-50"
                >
                  {isUpdating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : step.status === 'pending' ? (
                    'Begin Step'
                  ) : (
                    'Mark Complete'
                  )}
                </button>
              )}
              <button
                onClick={() => onStatusChange('skipped')}
                disabled={isUpdating}
                className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 disabled:opacity-50"
              >
                Skip
              </button>
            </div>
          )}

          {step.completedAt && (
            <p className="text-xs text-gray-500 mt-2">
              Completed: {new Date(step.completedAt).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    active: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    abandoned: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] ?? colors.draft}`}>
      {status}
    </span>
  );
}
